import { useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Tick02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Show } from "shared/components";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { cx } from "shared/utils";
import type { ApplicationStage } from "views/settings-crm/types/stage";
import type { Application } from "types/application";
import { EmptyStagePill, StagePill } from "./StagePill";
import { formatStageChange } from "./utils";

type StageChange = NonNullable<Application["stage_changed"]>;

const menuItemClass =
  "flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-xs font-medium text-content-1000 transition hover:bg-alpha-40 focus:outline-none focus-visible:bg-alpha-40";

export const ApplicationStagePicker = ({
  value,
  changed,
  stages,
  onChange,
  placement = "bottom-start",
  disabled = false,
  className
}: {
  value?: number | null;
  changed?: StageChange | null;
  stages: ApplicationStage[];
  onChange: (stageId: number | null) => void;
  placement?: "bottom-start" | "bottom-end";
  disabled?: boolean;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);

  if (!stages.length) return null;

  const stageId = value ?? null;
  const stage = stages.find((option) => option.id === stageId);
  const changedLabel = stage && changed ? formatStageChange(changed) : null;

  const pick = (next: number | null) => {
    setOpen(false);
    if (next !== stageId) onChange(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} placement={placement}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={stage ? `Stage: ${stage.name}` : "Set stage"}
          title={changedLabel ? `Set by ${changedLabel}` : undefined}
          disabled={disabled}
          className={cx(
            "inline-flex shrink-0 rounded-2xl transition hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 disabled:cursor-wait disabled:opacity-60",
            className
          )}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((isOpen) => !isOpen);
          }}
        >
          {stage ? (
            <StagePill stage={stage} chevron />
          ) : (
            <EmptyStagePill chevron />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="z-20 min-w-[240px] rounded-lg border border-alpha-100 bg-white p-1 shadow-lg focus:outline-none">
        <div role="menu" className="space-y-0.5">
          {stages.map((option) => {
            const selected = option.id === stageId;
            return (
              <button
                key={option.id}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                className={cx(menuItemClass, selected && "bg-alpha-40")}
                onClick={(e) => {
                  e.stopPropagation();
                  pick(option.id);
                }}
              >
                <span className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className="inline-flex size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: option.color }}
                  />
                  {option.name}
                </span>
                <Show if={selected}>
                  <HugeiconsIcon
                    icon={Tick02Icon}
                    size={14}
                    strokeWidth={2}
                    className="shrink-0 text-primary-500"
                  />
                </Show>
              </button>
            );
          })}
          <Show if={Boolean(stage)}>
            <div aria-hidden className="my-1 border-t border-alpha-40" />
            <button
              type="button"
              role="menuitem"
              className={menuItemClass}
              onClick={(e) => {
                e.stopPropagation();
                pick(null);
              }}
            >
              <span className="flex items-center gap-2">
                <span
                  aria-hidden
                  className="inline-flex size-2 shrink-0 rounded-full border border-dashed border-alpha-200"
                />
                No stage
              </span>
            </button>
          </Show>
          <Show if={Boolean(changedLabel)}>
            <p className="px-3 pt-2 pb-1 text-xxs text-content-400">
              Set by {changedLabel}
            </p>
          </Show>
        </div>
      </PopoverContent>
    </Popover>
  );
};
